import { useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowUpFromLine,
  CalendarX2,
  ChevronDown,
  Upload,
  FileSpreadsheet,
  FileText,
  IndianRupee,
  MessageSquare,
  Users,
  Wallet,
} from 'lucide-react';

import { ExportTablePrint, type PrintableTable } from '@/components/ExportTablePrint';
import { Menu } from '@/components/ui/Menu';
import { Select } from '@/components/ui/Select';
import { QueryError } from '@/components/ui/QueryError';
import { StatCard } from '@/components/ui/StatCard';
import {
  ROSTER_CATEGORY_OPTIONS,
  RosterFilters,
  rosterCategoryPatch,
} from '@/components/RosterFilters';
import { CONSOLE_PALETTE } from '@/features/institute/theme';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { classesApi } from '@/features/institute/classes/api/classes.api';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { instituteToday } from '@/features/teacher/utils';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import { formatCurrency } from '@/lib/format';
import { downloadXlsx } from '@/lib/xlsx';
import { cn } from '@/lib/utils';
import type {
  FeeAccountListParams,
  FeeAccountSort,
  FeeHead,
  FeeStatus,
  StudentFeeAccount,
} from '@/types/fees';
import { feesApi } from '../api/fees.api';
import { CollectFeeModal } from '../components/CollectFeeModal';
import { ClassFeesModal } from '../components/ClassFeesModal';
import { FeeChaseList } from '../components/FeeChaseList';
import { FeeCollectionOverview } from '../components/FeeCollectionOverview';
import { FeeQuickActions } from '../components/FeeQuickActions';
import { FeeCollectionsPanel } from '../components/FeeCollectionsPanel';
import { FeeRegisterPanel } from '../components/FeeRegisterPanel';
import { describeFeeSummary, feeSummaryScope, isFeeSummaryNarrowed } from '../summaryScope';
import {
  FeeReminderModal,
  reminderFamilyOf,
  type ReminderTarget,
} from '../components/FeeReminderModal';
import { PickStudentModal } from '../components/PickStudentModal';
import { SetFeeChargeModal } from '../components/SetFeeChargeModal';
import { StudentFeeAccountModal } from '../components/StudentFeeAccountModal';
import {
  FEE_ACCOUNTS_PER_PAGE,
  FEE_HEAD_LABEL,
  FEE_HEAD_OPTIONS,
  FEE_PAGE_SIZES,
  FEE_SORT_OPTIONS,
  FEE_STATUS_OPTIONS,
  FEE_STATUS_ORDER,
} from '../constants';
import { buildCollections, buildFeeRegister } from '../export';

/** A share of the session's receivable, as the summary tiles quote it. */
function shareOf(part: string | undefined, whole: string | undefined): string {
  const total = Number(whole ?? 0);
  if (total <= 0) return 'Nothing charged yet';

  return `${((Number(part ?? 0) / total) * 100).toFixed(1)}% of total receivable`;
}

const FEE_VIEWS = ['register', 'collections'] as const;
type FeeView = (typeof FEE_VIEWS)[number];

/** The ways a column-header click can order the register. */
const SORT_DIRECTIONS = ['asc', 'desc'] as const;

/**
 * Which panel is open, kept in the address so Back from anything opened on the
 * day book returns to the day book rather than to the register.
 */
type Filters = {
  tab: FeeView;
};

const DEFAULT_FILTERS: Filters = {
  tab: 'register',
};

const FILTER_OPTIONS = {
  allowed: {
    tab: FEE_VIEWS,
  },
};

/**
 * The register's filters, as they sit in the address bar under `register.`.
 *
 * `head` travels with them because it is part of what the reader chose to look
 * at, but it is not a narrowing — it is taken off again before the request, so
 * the server's totals are counted exactly as they were. `sortDirection` is
 * empty until a column header is clicked: a sort picked from the menu reads its
 * own natural way.
 */
type RegisterFilters = {
  page: number;
  limit: number;
  search: string;
  classUuid: string;
  sectionUuid: string;
  status: FeeStatus | '';
  gender: 'MALE' | 'FEMALE' | '';
  newThisYear: 'true' | '';
  sort: FeeAccountSort;
  sortDirection: (typeof SORT_DIRECTIONS)[number] | '';
  head: FeeHead;
};

const DEFAULT_REGISTER_FILTERS: RegisterFilters = {
  page: 1,
  limit: FEE_ACCOUNTS_PER_PAGE,
  search: '',
  classUuid: '',
  sectionUuid: '',
  status: '',
  gender: '',
  newThisYear: '',
  sort: 'balance',
  sortDirection: '',
  head: '',
};

/**
 * What a link may set. Class and section are record uuids and have no list —
 * the server scopes them. The category dropdown's two keys are read off the
 * dropdown itself, so they cannot drift from what it offers.
 */
const REGISTER_FILTER_OPTIONS = {
  prefix: 'register',
  allowed: {
    limit: FEE_PAGE_SIZES,
    status: FEE_STATUS_ORDER,
    gender: ROSTER_CATEGORY_OPTIONS.map((option) => rosterCategoryPatch(option.value).gender),
    newThisYear: ROSTER_CATEGORY_OPTIONS.map(
      (option) => rosterCategoryPatch(option.value).newThisYear,
    ),
    sort: FEE_SORT_OPTIONS.map((option) => option.value),
    sortDirection: SORT_DIRECTIONS,
    head: Object.keys(FEE_HEAD_LABEL),
  },
};

/**
 * The fee counter, for the institute's current session.
 *
 * One register with a rail beside it: the table answers "who owes what", the
 * five figures above it answer "how is the year collecting", and the rail is
 * everything an office does *after* reading either — where the money sits, who
 * to ring, and the way on. The day book, which answers the different question
 * of "what came in", opens over the same panel from the rail.
 *
 * Scoped to one session throughout — a list mixing last year's arrears with this
 * year's collection answers nobody's question, and the server decides which
 * session that is.
 *
 * It reads the same roll as the student register and works the same at a school
 * and at a college: a school charges a class and places children in sections, so
 * the register groups by both; a college has neither, and the columns that would
 * be dashes on every row are simply not drawn. What is charged is per *student*
 * either way, which is why one screen serves both.
 *
 * Three permissions decide what is offered here, and the server enforces every
 * one of them again: `FEE_VIEW` to be on this page at all, `FEE_COLLECT` to take
 * money, `FEE_MANAGE` to decide what a family is charged.
 */
export function FeesPage() {
  const permissions = useAuthStore((state) => state.user?.permissions);
  const canView = Boolean(permissions?.includes('FEE_VIEW'));
  const canCollect = Boolean(permissions?.includes('FEE_COLLECT'));
  const canManage = Boolean(permissions?.includes('FEE_MANAGE'));

  /**
   * Chasing a family needs both keys, and the server requires both too:
   * `FEE_VIEW` because deciding who owes is reading the register, and
   * `MESSAGE_SEND` because this reaches people outside the product, costs the
   * institute money and cannot be recalled.
   */
  const canRemind = canView && Boolean(permissions?.includes('MESSAGE_SEND'));

  const branding = useInstituteBranding((state) => state.branding);
  const isCollege = branding?.institutionType === 'COLLEGE';

  const [pageFilters, setPageFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const view = pageFilters.tab;
  const setView = (next: FeeView) => setPageFilters({ tab: next });
  const [sessionName, setSessionName] = useState<string | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  /**
   * What the register is showing.
   *
   * Held here rather than inside the panel because the toolbar above it exports
   * whatever is on screen, and an export that quietly saved a different set of
   * rows from the one being read is worse than no export at all.
   */
  const [registerFilters, setRegisterFilters] = useUrlFilters(
    DEFAULT_REGISTER_FILTERS,
    REGISTER_FILTER_OPTIONS,
  );

  // Built field by field, leaving `head` out, and memoised: the summary query
  // below compares it by reference, so a fresh object every render would
  // re-run that request on every render.
  const filters = useMemo<FeeAccountListParams>(
    () => ({
      page: registerFilters.page,
      limit: registerFilters.limit,
      search: registerFilters.search,
      classUuid: registerFilters.classUuid,
      sectionUuid: registerFilters.sectionUuid,
      status: registerFilters.status,
      gender: registerFilters.gender,
      newThisYear: registerFilters.newThisYear,
      sort: registerFilters.sort,
      sortDirection: registerFilters.sortDirection || undefined,
    }),
    [registerFilters],
  );

  /** Any change but the page itself sends the reader back to the first page. */
  function updateFilters(patch: Partial<FeeAccountListParams>) {
    // A sort picked from the menu reads its own natural way; a direction left
    // over from a column-header click would turn "Name" into Z to A.
    const resetsDirection = patch.sort !== undefined && patch.sortDirection === undefined;
    setRegisterFilters({
      ...patch,
      ...(resetsDirection ? { sortDirection: '' } : {}),
      ...(patch.page ? {} : { page: 1 }),
    });
  }

  /** Which head of the bill the register's money columns report. */
  const { head } = registerFilters;
  const setHead = (next: FeeHead) => setRegisterFilters({ head: next });

  /**
   * Bumped whenever money moves, so both panels reload rather than showing a
   * balance that has just changed under them. One token for both: a receipt
   * taken in the account dialog belongs in the day book as much as it changes
   * the register.
   */
  const [reloadToken, setReloadToken] = useState(0);
  const reload = () => setReloadToken((token) => token + 1);

  /** Which student's account is open, and which dialog is over it. */
  const [openStudentUuid, setOpenStudentUuid] = useState<string | null>(null);
  const [collectingFrom, setCollectingFrom] = useState<StudentFeeAccount | null>(null);
  const [editingCharge, setEditingCharge] = useState<StudentFeeAccount | null>(null);
  /** Whose account a row action is loading, so only that row's button spins. */
  const [busyStudentUuid, setBusyStudentUuid] = useState<string | null>(null);
  /** Who is being chased — one family, or everybody who owes. */
  const [reminding, setReminding] = useState<ReminderTarget | null>(null);
  /** Open while the counter is asking which family is paying. */
  const [isPicking, setIsPicking] = useState(false);
  /** The read-only list of what each class is charged. */
  const [isViewingClassFees, setIsViewingClassFees] = useState(false);
  /** True while an export is walking the register, so it cannot be started twice. */
  const [isExporting, setIsExporting] = useState(false);
  /** The table being saved as a PDF; set only while the print dialog is up. */
  const [printing, setPrinting] = useState<PrintableTable | null>(null);

  /**
   * A row action goes straight to the form it names.
   *
   * Both forms need the account itself — what is outstanding on each head, what
   * has already been collected — so it is loaded first rather than opening a
   * dialog full of blanks that fill in a moment later.
   */
  async function openFormFor(studentUuid: string, form: 'collect' | 'charge') {
    setBusyStudentUuid(studentUuid);

    try {
      const account = await feesApi.getAccount(studentUuid);
      // The picker, if it was open, closes as the counter opens: two stacked
      // native `<dialog>`s would trap focus in the wrong one.
      setIsPicking(false);
      if (form === 'collect') setCollectingFrom(account);
      else setEditingCharge(account);
    } catch (caught) {
      toast.error(
        'Could not open this account',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setBusyStudentUuid(null);
    }
  }

  /**
   * Saves the register or the day book as Excel or PDF, and says what it saved.
   *
   * One table for both formats, built from the same scoped reads, so the PDF
   * and the workbook cannot disagree about who is on them.
   */
  async function runExport(what: 'register' | 'collections', format: 'xlsx' | 'pdf') {
    setIsExporting(true);

    try {
      const session = (currentSession ?? 'session').replaceAll('/', '-');
      const table = what === 'register' ? await buildFeeRegister(filters) : await buildCollections();
      const noun = what === 'register' ? 'accounts' : 'receipts';

      if (table.rows.length === 0) {
        toast.info(
          'Nothing to export',
          what === 'register'
            ? 'No accounts match these filters. Widen them and try again.'
            : 'No receipts have been issued this session yet.',
        );
        return;
      }

      const saved = `${table.rows.length.toLocaleString('en-IN')} ${noun}`;
      // Never silently trimmed: an office that thinks it exported everything
      // will reconcile against a file that is missing rows.
      const trimmed =
        table.skipped > 0
          ? `${table.skipped.toLocaleString('en-IN')} more could not fit in one file. Narrow the filters and export again.`
          : null;
      const fileName = `${what === 'register' ? 'fee-register' : 'fee-collections'}-${session}`;
      const title = `${what === 'register' ? 'Fee register' : 'Collections day book'} ${session}`;

      if (format === 'xlsx') {
        downloadXlsx(`${fileName}.xlsx`, title, [table.header, ...table.rows]);
        toast.success(`${saved} saved`, trimmed ?? 'Check your downloads folder.');
      } else {
        // The print dialog is the confirmation; only a shortfall needs saying.
        setPrinting({ ...table, title, fileName });
        if (trimmed) toast.info(`Only ${saved} in this PDF`, trimmed);
      }
    } catch (caught) {
      toast.error(
        'Could not build the file',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsExporting(false);
    }
  }

  // The classes drive the register's filter row. They fail independently of the
  // register: an institute whose class list is slow should still see its
  // accounts. A college has none, so the call is skipped.
  const {
    data: classes,
    isLoading: areClassesLoading,
    error: classesError,
    refetch: refetchClasses,
  } = useApiQuery(
    () => (isCollege ? Promise.resolve([]) : classesApi.list()),
    [isCollege],
  );

  /**
   * The session's ledger, for the five figures above the table.
   *
   * Its own request rather than figures lifted out of the register panel, and
   * that is the point of it: the panel's totals follow the panel's filters, so
   * narrowing to one class would quietly change a headline an office quotes to
   * a trustee. This asks for the whole session and five rows — the totals come
   * back regardless of the page size — and it survives the switch to the day
   * book, where the register is not even mounted.
   *
   * Sorted by balance, so the same response also feeds "Who to chase": the
   * family that owes the most is the one to ring first.
   */
  const { data: ledger, isLoading: isLedgerLoading, error: ledgerError } = useApiQuery(
    () => feesApi.listAccounts({ page: 1, limit: 5, sort: 'balance' }),
    [reloadToken],
  );

  /**
   * Today, taken once for the whole screen.
   *
   * The countdowns in the reminders panel all measure from it, so two rows can
   * never be a day apart because the clock turned over between renders. The
   * school's day, not UTC: before 5:30 am IST that was still yesterday, and a
   * fee due today read as not yet due.
   */
  const today = instituteToday();

  /**
   * The session's name, for the exports and the receivable card's hint.
   *
   * The register panel reports it as it loads, but on the day book that panel
   * is never mounted — the ledger above is, and it carries the same session.
   */
  const currentSession = sessionName ?? ledger?.academicYear.name ?? null;

  /**
   * What the five cards are counted over: the filters above the table, so the
   * totals always describe the students the register is showing. The hints
   * then name what is being counted, so a narrowed figure cannot be quoted as
   * the school's. See `summaryScope.ts` for why the fee status is left out.
   */
  const summaryFilters = useMemo(() => feeSummaryScope(filters, head), [filters, head]);
  const isSummaryNarrowed = isFeeSummaryNarrowed(summaryFilters);
  const summaryScope = describeFeeSummary(summaryFilters, classes ?? []);

  /**
   * The narrowed figures. Only asked for once a filter is set — unfiltered, the
   * whole-session ledger above already holds them. One row is enough: the
   * totals come back whatever the page size.
   */
  const { data: narrowedSummary, isLoading: isNarrowedLoading, error: narrowedError } = useApiQuery(
    () =>
      isSummaryNarrowed
        ? feesApi.listAccounts({ page: 1, limit: 1, ...summaryFilters })
        : Promise.resolve(null),
    [isSummaryNarrowed, summaryFilters, reloadToken],
  );
  const summary = isSummaryNarrowed ? narrowedSummary : ledger;
  const isSummaryLoading = isSummaryNarrowed ? isNarrowedLoading : isLedgerLoading;
  const summaryError = isSummaryNarrowed ? narrowedError : ledgerError;
  // A failed request is not an empty ledger: the cards show a dash, never ₹0.
  const isSummaryUnavailable = summaryError !== null;
  const unavailable = '—';
  const unavailableHint = 'Could not be loaded';

  /** Accounts that still owe something — what the chase list counts down from. */
  const owingCount = ledger ? (ledger.byStatus.UNPAID ?? 0) + (ledger.byStatus.PARTIAL ?? 0) : 0;

  const unpaidCount = summary?.byStatus.UNPAID ?? 0;

  /*
    The money cards open the register on every account, ordered by the figure
    they total — an account can hold one fee status at a time, so "collected"
    or "outstanding" has no single status to filter on, but it does have a
    column. Like "Not paid", the cards' narrowing goes with them.
  */
  function openRegisterSortedBy(sort: FeeAccountSort) {
    openRegisterWith({ status: '', sort });
  }

  /* Opens the register on what a card counted — the cards already share its filters. */
  function openRegisterWith(patch: Partial<FeeAccountListParams>) {
    setView('register');
    updateFilters(patch);
  }
  const isRegisterSortedBy = (sort: FeeAccountSort) => !filters.status && filters.sort === sort;

  return (
    <div className={cn('flex flex-col gap-6 w-full max-w-full overflow-hidden', CONSOLE_PALETTE)}>
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          {view === 'collections' && (
            <div className="flex items-center gap-2 text-sm font-medium mb-1.5">
              <button
                type="button"
                onClick={() => setView('register')}
                className="inline-flex items-center gap-1.5 text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 hover:underline transition-colors cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Fees</span>
              </button>
              <span className="text-slate-300 dark:text-slate-600 font-normal">/</span>
              <span className="text-slate-900 dark:text-white font-bold">
                Collections day book
              </span>
            </div>
          )}
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            {view === 'collections' ? 'Collections day book' : 'Fees'}
          </h1>
          <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
            {view === 'collections'
              ? 'Every receipt issued this session, searchable by receipt number, student or date.'
              : 'Track and manage student fee collections, reminders and reports.'}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
          {view === 'collections' && (
            <button
              type="button"
              onClick={() => setView('register')}
              className="rounded-xl bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-[#16253d] text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white px-4 py-2.5 border border-slate-200 dark:border-slate-800 text-sm font-bold inline-flex items-center gap-2 transition-all cursor-pointer shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <ArrowLeft className="h-4 w-4 text-slate-500" />
              <span>Back to student accounts</span>
            </button>
          )}
 {canView && (
            <Menu
              label="Export fee data"
              align="end"
              disabled={isExporting}
              trigger={
                <span className="inline-flex items-center gap-2 font-bold text-slate-700 dark:text-slate-200">
                  <Upload className="h-4 w-4 text-slate-500" aria-hidden />
                  <span>{isExporting ? 'Exporting…' : 'Export'}</span>
                  <ChevronDown className="h-4 w-4 text-slate-400" aria-hidden />
                </span>
              }
              triggerClassName="rounded-xl bg-white dark:bg-[#111c2e] hover:bg-slate-50 dark:hover:bg-[#16253d] text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white px-4 py-2.5 border border-slate-200 dark:border-slate-800 text-sm font-bold inline-flex items-center gap-2 transition-all cursor-pointer shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
              items={[
                {
                  key: 'register-xlsx',
                  label: 'Fee register — Excel',
                  description: 'Every account, as this screen is filtered',
                  icon: <FileSpreadsheet className="h-4 w-4" />,
                  onSelect: () => void runExport('register', 'xlsx'),
                },
                {
                  key: 'register-pdf',
                  label: 'Fee register — PDF',
                  description: 'The same accounts, ready to print or save',
                  icon: <FileText className="h-4 w-4" />,
                  onSelect: () => void runExport('register', 'pdf'),
                },
                {
                  key: 'collections-xlsx',
                  label: 'Collections day book — Excel',
                  description: 'Every receipt issued this session',
                  icon: <FileSpreadsheet className="h-4 w-4" />,
                  onSelect: () => void runExport('collections', 'xlsx'),
                },
                {
                  key: 'collections-pdf',
                  label: 'Collections day book — PDF',
                  description: 'The same receipts, ready to print or save',
                  icon: <FileText className="h-4 w-4" />,
                  onSelect: () => void runExport('collections', 'pdf'),
                },
              ]}
            />
          )}

          {canRemind && view === 'register' && (
            <button
              type="button"
              onClick={() => setReminding({ kind: 'ALL_DUE' })}
              className="rounded-xl bg-amber-50 hover:bg-amber-100/90 dark:bg-amber-950/40 dark:hover:bg-amber-900/60 text-amber-900 dark:text-amber-200 hover:text-amber-950 dark:hover:text-white px-4 py-2.5 border border-amber-200/90 dark:border-amber-800/60 text-sm font-bold inline-flex items-center gap-2 transition-all cursor-pointer shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            >
              <MessageSquare className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span>Fee reminders</span>
            </button>
          )}

         
          {canCollect && (
            <button
              type="button"
              onClick={() => setIsPicking(true)}
              className="rounded-xl bg-indigo-600 tracking-wide hover:bg-indigo-700 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-2 transition-all cursor-pointer shadow-sm shadow-indigo-500/20 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              <IndianRupee className="h-4 w-4 stroke-[2.5]" />
              <span>Receive fee</span>
            </button>
          )}
        </div>
      </header>

      {/* The session at a glance, counted over the filters above the table. */}
      {summaryError && (
        <QueryError title="Could not load the fee totals" error={summaryError} onRetry={reload} />
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 w-full max-w-full">
        <StatCard
          label="Total students"
          value={isSummaryUnavailable ? unavailable : (summary?.meta.total ?? 0).toLocaleString('en-IN')}
          hint={summaryScope ?? (isCollege ? 'On this session’s roll' : 'All classes')}
          variant="cyan"
          icon={<Users className="h-5 w-5 text-white" />}
          isLoading={isSummaryLoading}
          isActive={isRegisterSortedBy('name')}
          linkLabel="Show every account, A to Z"
          onClick={() => openRegisterSortedBy('name')}
        />
        <StatCard
          label="Total receivable"
          value={isSummaryUnavailable ? unavailable : formatCurrency(summary?.totals.payable ?? 0)}
          hint={summaryScope ?? (currentSession ? `For session ${currentSession}` : 'For this session')}
          variant="indigo"
          icon={<IndianRupee className="h-5 w-5 text-white" />}
          isLoading={isSummaryLoading}
          isActive={isRegisterSortedBy('payable')}
          linkLabel="Show every account, largest payable first"
          onClick={() => openRegisterSortedBy('payable')}
        />
        <StatCard
          label="Total collected"
          value={isSummaryUnavailable ? unavailable : formatCurrency(summary?.totals.collected ?? 0)}
          hint={
            isSummaryUnavailable ? unavailableHint : shareOf(summary?.totals.collected, summary?.totals.payable)
          }
          variant="emerald"
          icon={<Wallet className="h-5 w-5 text-white" />}
          isLoading={isSummaryLoading}
          isActive={isRegisterSortedBy('collected')}
          linkLabel="Show every account, most collected first"
          onClick={() => openRegisterSortedBy('collected')}
        />
        <StatCard
          label="Total outstanding"
          value={isSummaryUnavailable ? unavailable : formatCurrency(summary?.totals.balance ?? 0)}
          hint={
            isSummaryUnavailable ? unavailableHint : shareOf(summary?.totals.balance, summary?.totals.payable)
          }
          variant="amber"
          icon={<ArrowUpFromLine className="h-5 w-5 text-white" />}
          isLoading={isSummaryLoading}
          isActive={isRegisterSortedBy('balance')}
          linkLabel="Show every account, largest balance first"
          onClick={() => openRegisterSortedBy('balance')}
        />
        {/*
          The one tile that filters rather than sorts: nothing collected at all
          is the list an office works through, so pressing it filters the
          register to exactly those accounts and pressing it again lets
          everybody back in.
        */}
        <StatCard
          label="Not paid"
          value={isSummaryUnavailable ? unavailable : formatCurrency(summary?.balanceByStatus.UNPAID ?? 0)}
          hint={
            isSummaryUnavailable
              ? unavailableHint
              : `${unpaidCount.toLocaleString('en-IN')} ${
                  unpaidCount === 1 ? 'student has' : 'students have'
                } paid nothing`
          }
          variant="rose"
          icon={<CalendarX2 className="h-5 w-5 text-white" />}
          isLoading={isSummaryLoading}
          isActive={filters.status === 'UNPAID'}
          linkLabel={
            filters.status === 'UNPAID'
              ? 'Stop showing only accounts that have paid nothing'
              : 'Show only accounts that have paid nothing'
          }
          onClick={() => {
            setView('register');
            // The list the card counted: its narrowing goes with it, so pressing
            // "3 students in Class 5" opens those three, not the whole school's.
            if (filters.status === 'UNPAID') updateFilters({ status: '' });
            else openRegisterWith({ status: 'UNPAID' });
          }}
        />
      </div>

      {view === 'register' ? (
        <div className="flex flex-col gap-6 w-full">
          {/* Full-width filter bar outside the table box */}
          <RosterFilters
            variant="bar"
            hideBorder
            filters={filters}
            classes={classes ?? []}
            hasPlacement={!isCollege}
            searchLabel="Search the fee register"
            searchPlaceholder={
              isCollege ? 'Search by name or admission no.' : 'Search by name, admission no. or roll no.'
            }
            statusLabel="Fee status"
            statusOptions={FEE_STATUS_OPTIONS}
            sortOptions={FEE_SORT_OPTIONS}
            onChange={updateFilters}
            extraControls={
              <Select
                label="Fee type"
                labelClassName="text-xs font-bold text-slate-700 dark:text-slate-200"
                options={FEE_HEAD_OPTIONS}
                className="!rounded-xl !h-9 !py-1 !px-3 !text-[13px] !font-medium !text-slate-700 dark:!text-slate-200 !bg-white dark:!bg-[#111c2e] !border-slate-200 dark:!border-slate-700/80 shadow-2xs hover:!border-indigo-300 transition-colors"
                value={head}
                onChange={(event) => setHead(event.target.value as FeeHead)}
              />
            }
          />

          {/* Below filters: Full-width Table, followed by Overview cards below */}
          <div className="flex flex-col gap-6">
            <div ref={tableRef} className="w-full scroll-mt-20">
              <FeeRegisterPanel
                isCollege={isCollege}
                canCollect={canCollect}
                canManage={canManage}
                canRemind={canRemind}
                filters={filters}
                onFiltersChange={updateFilters}
                head={head}
                onHeadChange={setHead}
                onOpenAccount={setOpenStudentUuid}
                onCollect={(studentUuid) => void openFormFor(studentUuid, 'collect')}
                onSetCharge={(studentUuid) => void openFormFor(studentUuid, 'charge')}
                busyStudentUuid={busyStudentUuid}
                onRemind={(account) =>
                  setReminding({
                    kind: 'STUDENT',
                    studentUuid: account.studentUuid,
                    session: currentSession,
                    ...reminderFamilyOf(account),
                  })
                }
                reloadToken={reloadToken}
                onLoaded={(academicYear) => setSessionName(academicYear.name)}
              />
            </div>

            {/* Below table: Fee collection overview, who to chase, quick actions */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              <FeeCollectionOverview
                totals={ledger?.totals ?? null}
                balanceByStatus={ledger?.balanceByStatus ?? null}
                isLoading={isLedgerLoading}
              />

              <FeeChaseList
                accounts={ledger?.items ?? null}
                today={today}
                isLoading={isLedgerLoading}
                owingCount={owingCount}
                canRemind={canRemind}
                onRemind={(account) =>
                  setReminding({
                    kind: 'STUDENT',
                    studentUuid: account.studentUuid,
                    session: currentSession,
                    ...reminderFamilyOf(account),
                  })
                }
                onOpenAccount={setOpenStudentUuid}
                onSeeAll={() => {
                  setView('register');
                  // Every account, largest balance first — the not-paid and
                  // part-paid ones the list counts lead, in the list's own
                  // order. "Not paid" alone would drop every part-paid family
                  // the count included.
                  updateFilters({ status: '', sort: 'balance' });
                  setTimeout(() => {
                    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 0);
                }}
              />

              <FeeQuickActions
                canCollect={canCollect}
                canRemind={canRemind}
                canView={canView}
                canManage={canManage}
                onCollectFee={() => setIsPicking(true)}
                onRemindEverybody={() => setReminding({ kind: 'ALL_DUE' })}
                onDownloadReport={() => void runExport('register', 'xlsx')}
                isDownloading={isExporting}
                onOpenDayBook={() => setView('collections')}
                onOpenFeeSettings={isCollege ? undefined : () => setIsViewingClassFees(true)}
              />
            </div>
          </div>
        </div>
      ) : (
        <FeeCollectionsPanel
          isCollege={isCollege}
          canCollect={canCollect}
          onOpenAccount={setOpenStudentUuid}
          reloadToken={reloadToken}
          onChanged={reload}
        />
      )}

      {/* Which family is paying, when the counter was opened from the toolbar
          rather than from a row. It closes as the counter opens. */}
      <ClassFeesModal
        isOpen={isViewingClassFees}
        onClose={() => setIsViewingClassFees(false)}
        classes={classes}
        isLoading={areClassesLoading}
        error={classesError}
        onRetry={refetchClasses}
        // A class's fee is changed in the class's own setup, under its own key.
        canEdit={Boolean(permissions?.includes('CLASS_MANAGE'))}
        canManageInstallments={canManage}
      />

      <PickStudentModal
        isOpen={isPicking && collectingFrom === null}
        onClose={() => setIsPicking(false)}
        onPick={(studentUuid) => void openFormFor(studentUuid, 'collect')}
        busyStudentUuid={busyStudentUuid}
      />

      {/*
        The account dialog is the hub: every action on this screen opens it
        first, so nobody takes money without having seen what is owed. It closes
        while the collect and edit dialogs are up — two stacked native
        `<dialog>`s would trap focus in the wrong one.
      */}
      <StudentFeeAccountModal
        studentUuid={openStudentUuid}
        isOpen={
          openStudentUuid !== null &&
          collectingFrom === null &&
          editingCharge === null &&
          reminding === null
        }
        onClose={() => setOpenStudentUuid(null)}
        canCollect={canCollect}
        canManage={canManage}
        canRemind={canRemind}
        onCollect={setCollectingFrom}
        onEditCharge={setEditingCharge}
        onRemind={(account) =>
          setReminding({
            kind: 'STUDENT',
            studentUuid: account.student.uuid,
            session: account.academicYear.name,
            fullName: account.student.fullName,
            balance: account.balance,
            admissionNumber: account.student.admissionNumber,
            installment: account.nextInstallment?.name ?? null,
            // The account's own date, else the next installment's — the date the
            // server writes into the reminder.
            dueDate: account.dueDate ?? account.nextInstallment?.dueDate ?? null,
          })
        }
        onChanged={reload}
      />

      <CollectFeeModal
        account={collectingFrom}
        isOpen={collectingFrom !== null}
        onClose={() => {
          setCollectingFrom(null);
          setOpenStudentUuid(null);
        }}
        onBack={
          openStudentUuid !== null
            ? () => {
                setCollectingFrom(null);
              }
            : undefined
        }
        onCollected={(receipt, account) => {
          setCollectingFrom(null);
          reload();
          // Raised after the dialog closes — a toast under an open `<dialog>`
          // sits behind it, in the browser's top layer.
          toast.success(
            `Receipt ${receipt.receiptNumber} — ${formatCurrency(receipt.amount)}`,
            Number(account.balance) > 0
              ? `${formatCurrency(account.balance)} still outstanding for ${account.student.fullName}.`
              : `${account.student.fullName} has nothing outstanding for the session.`,
          );
        }}
      />

      {/* Mounted only while it is open, so each send starts on a fresh
          template rather than on whatever the last one was left saying. */}
      {reminding && (
        <FeeReminderModal target={reminding} onClose={() => setReminding(null)} onSent={reload} />
      )}

      <SetFeeChargeModal
        account={editingCharge}
        isOpen={editingCharge !== null}
        onClose={() => {
          setEditingCharge(null);
          setOpenStudentUuid(null);
        }}
        onBack={
          openStudentUuid !== null
            ? () => {
                setEditingCharge(null);
              }
            : undefined
        }
        onSaved={(account, wasOpened) => {
          setEditingCharge(null);
          reload();
          toast.success(
            wasOpened
              ? `Fee account opened for ${account.student.fullName}`
              : `Fees updated for ${account.student.fullName}`,
            `${formatCurrency(account.payable)} payable for the ${account.academicYear.name} session.`,
          );
        }}
      />

      <ExportTablePrint table={printing} onFinished={() => setPrinting(null)} />
    </div>
  );
}
